import { pauseAllMedia } from '@/scripts/core/global'
import { type DeferredMedia } from '@/scripts/theme/deferred-media'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class ModalDialog extends UcoastEl {
	static htmlSelector = 'modal-dialog'
	moved: boolean = false
	openedBy?: HTMLElement
	closeAnimationCleanup?: () => void
	constructor() {
		super()
		this.querySelectorAll('[id^="ModalClose-"]')?.forEach((closeButton) => {
			closeButton.addEventListener('click', this.hide.bind(this, false))
		})
		this.addEventListener('keyup', (event) => {
			if (event.code?.toUpperCase() === 'ESCAPE') this.hide()
		})
		if (this.classList.contains('media-modal')) {
			this.addEventListener('pointerup', (event) => {
				if (
					event.pointerType === 'mouse' &&
					!q.oClosestTarget(
						event,
						'deferred-media, product-model'
					)
				)
					this.hide()
			})
		} else {
			this.addEventListener('click', (event) => {
				if (event.target === this) this.hide()
			})
		}
	}

	override connectedCallback() {
		if (this.moved) return
		this.moved = true
		if (this.isInline()) {
		}
		document.body.appendChild(this)
	}

	getHtmlSelector() {
		return ModalDialog.htmlSelector
	}

	isInline(): boolean {
		if (
			this.hasAttribute('data-no-desktop-move') &&
			window.matchMedia('(min-width: 750px)').matches
		) {
			return true
		}
		if (this.getHtmlSelector() === 'welcome-popup') {
			return true
		}

		return false
	}

	show(opener: HTMLElement) {
		this.cancelPendingClose()
		this.openedBy = opener
		const popup = q.os<DeferredMedia>('.template-popup', this)
		if (!this.isInline()) {
			document.body.classList.add('overflow-hidden')
		}
		this.setAttribute('open', '')
		void this.offsetWidth
		this.setAttribute('data-uc-modal-open', '')
		if (popup) popup.loadContent()
		window.TsDOM.trapFocus(this, q.rs('[role="dialog"]', this))
		if (!this.isInline() && !this.hasAttribute('data-preserve-media')) {
			pauseAllMedia()
		}
	}

	hide(preventFocus = false) {
		if (!this.hasAttribute('open') || this.closeAnimationCleanup) return
		if (preventFocus) this.openedBy = undefined
		this.removeAttribute('data-uc-modal-open')
		const hasOpacityTransition = getComputedStyle(this)
			.transitionDuration.split(',')
			.some((duration) => Number.parseFloat(duration) > 0)
		if (!hasOpacityTransition) {
			this.finishHide()
			return
		}

		const handleTransitionEnd = (event: TransitionEvent) => {
			if (event.target !== this || event.propertyName !== 'opacity') return
			this.finishHide()
		}
		this.addEventListener('transitionend', handleTransitionEnd)
		const fallback = window.setTimeout(() => this.finishHide(), 300)
		this.closeAnimationCleanup = () => {
			this.removeEventListener('transitionend', handleTransitionEnd)
			window.clearTimeout(fallback)
			this.closeAnimationCleanup = undefined
		}
	}

	private finishHide() {
		this.cancelPendingClose()
		const otherOpenModals = Array.from(
			document.querySelectorAll<ModalDialog>('modal-dialog[open]')
		).filter((modal) => modal !== this)
		const otherOpenModal = otherOpenModals[0]
		const otherOpenBlockingModal = otherOpenModals.find(
			(modal) => !modal.isInline()
		)
		if (
			!this.isInline() &&
			!otherOpenBlockingModal
		) {
			document.body.classList.remove('overflow-hidden')
		}
		document.body.dispatchEvent(new CustomEvent('modalClosed'))
		this.removeAttribute('open')
		this.removeAttribute('data-uc-modal-open')
		window.TsDOM.removeTrapFocus(this.openedBy)
		if (otherOpenModal) {
			const focusTarget =
				this.openedBy && otherOpenModal.contains(this.openedBy)
					? this.openedBy
					: q.rs<HTMLElement>('[role="dialog"]', otherOpenModal)
			window.TsDOM.trapFocus(otherOpenModal, focusTarget)
		}
		if (!this.isInline() && !this.hasAttribute('data-preserve-media')) {
			pauseAllMedia()
		}
		this.onHidden()
	}

	protected onHidden() {}

	private cancelPendingClose() {
		this.closeAnimationCleanup?.()
	}

	override disconnectedCallback() {
		if (this.hasAttribute('open')) this.finishHide()
		else this.cancelPendingClose()
		super.disconnectedCallback()
	}
}
